import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import type { Folder, User } from '@/types/api';
import { useToast } from '@/hooks/useToast';

interface UpdateFolderParams {
  folderId: string;
  title: string;
  syncToMobile: boolean;
  isPublic: boolean;
  formKey: string;
}

interface DeleteFolderParams {
  folderId: string;
  formKey: string;
}

interface ConvertFolderToTagParams {
  folderId: string;
  formKey: string;
}

interface CreateFolderParams {
  title: string;
  formKey: string;
}

/**
 * Update folder details
 */
export async function updateFolder({
  folderId,
  title,
  syncToMobile,
  isPublic,
  formKey: _formKey, // Not needed for /data/ endpoints but kept for API consistency
}: UpdateFolderParams): Promise<Folder> {
  const response = await fetch(`/data/folders/${folderId}/update`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      title,
      sync_to_mobile: syncToMobile,
      public: isPublic,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to update folder: ${response.status}`);
  }

  const data = await response.json();
  return data.folder;
}

/**
 * Delete a folder
 */
export async function deleteFolder({
  folderId,
  formKey: _formKey, // Not needed for /data/ endpoints but kept for API consistency
}: DeleteFolderParams): Promise<void> {
  const response = await fetch(`/data/folders/${folderId}/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify({}), // Empty body for delete
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to delete folder: ${response.status}`);
  }
}

/**
 * Convert a folder to a tag
 */
export async function convertFolderToTag({
  folderId,
  formKey: _formKey, // Not needed for /data/ endpoints but kept for API consistency
}: ConvertFolderToTagParams): Promise<void> {
  const response = await fetch(`/data/folders/${folderId}/convert_to_tag`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify({}), // Empty body for convert
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to convert folder to tag: ${response.status}`);
  }
}

/**
 * Create a new folder
 */
export async function createFolder({
  title,
  formKey: _formKey, // Not needed for /data/ endpoints but kept for API consistency
}: CreateFolderParams): Promise<void> {
  const response = await fetch('/data/folders/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      title,
      sync_to_mobile: true, // Default to syncing to mobile
      public: false, // Default to private
    }),
  });

  if (!response.ok) {
    // Try to parse JSON error response
    try {
      const errorData = await response.json();
      if (errorData.errorMessage) {
        // If the error is a duplicate, include that in the error for special handling
        if (errorData.errorType === 'duplicate') {
          const error = new Error(errorData.errorMessage);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).type = 'duplicate';
          throw error;
        }
        throw new Error(errorData.errorMessage);
      }
    } catch (parseError) {
      // If it's already an Error from above, re-throw it
      if (parseError instanceof Error && parseError.message) {
        throw parseError;
      }
      // Otherwise fall back to text parsing
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Failed to create folder: ${response.status}`);
    }
  }
}

// Hook for updating a folder
export function useUpdateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateFolder,
    onSuccess: () => {
      // Invalidate user query to refresh folders list
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      // Also invalidate bookmarks in case folder title changed
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

// Hook for deleting a folder
export function useDeleteFolder() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: deleteFolder,
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: () => {
      // Invalidate user query to refresh folders list
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      // Invalidate bookmarks to refresh any folder-specific views
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

// Hook for converting folder to tag
export function useConvertFolderToTag() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: convertFolderToTag,
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: () => {
      // Invalidate both user (for folders) and tags
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      // Also invalidate bookmarks
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

interface ReorderFoldersParams {
  folderIds: string[];
  formKey: string;
}

type FolderSortMode = 'manual' | 'name' | 'oldest' | 'newest' | 'count' | 'recently_used';

interface UpdateFolderSortParams {
  sortMode: FolderSortMode;
  formKey: string;
}

const folderSortModeToBackendValue: Record<FolderSortMode, number> = {
  manual: 0,
  name: 1,
  oldest: 2,
  newest: 3,
  count: 4,
  recently_used: 5,
};

/**
 * Persist folder sort mode to backend session.
 */
export async function updateFolderSort({
  sortMode,
  formKey,
}: UpdateFolderSortParams): Promise<void> {
  const formData = new URLSearchParams();
  formData.append('folder_sort', folderSortModeToBackendValue[sortMode].toString());
  formData.append('form_key', formKey);

  const response = await fetch('/data/user/folder_sort', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: formData.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to update folder sort: ${response.status}`);
  }
}

/**
 * Reorder folders based on the array of folder IDs
 */
export async function reorderFolders({ folderIds }: ReorderFoldersParams): Promise<void> {
  // Use the /data/ endpoint which follows the established pattern
  const response = await fetch('/data/folders/reorder', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify(folderIds),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to reorder folders: ${response.status}`);
  }
}

// Hook for reordering folders with optimistic updates
export function useReorderFolders() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: reorderFolders,
    onMutate: async ({ folderIds }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['currentUser'] });

      // Snapshot the previous value
      const previousUser = queryClient.getQueryData(['currentUser']);

      // Optimistically update folders order
      queryClient.setQueryData(['currentUser'], (old: unknown) => {
        if (!old || typeof old !== 'object' || !('currentUser' in old)) return old;
        const userData = old as { currentUser: User };

        if (!userData.currentUser?.folders) return old;

        // Create new array with folders in the new order
        const foldersMap = userData.currentUser.folders.reduce<Record<string, Folder>>(
          (map, folder) => {
            map[folder.id] = folder;
            return map;
          },
          {},
        );

        const reorderedFolders = folderIds
          .map((id) => foldersMap[id])
          .filter(Boolean)
          .map((folder, index) => ({
            ...folder,
            position: index, // Update position based on new order
          }));

        return {
          ...old,
          currentUser: {
            ...userData.currentUser,
            folders: reorderedFolders,
          },
        };
      });

      return { previousUser };
    },
    onError: (_err, _variables, context) => {
      // Rollback to the previous value on error
      if (context?.previousUser) {
        queryClient.setQueryData(['currentUser'], context.previousUser);
      }
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

// Hook for updating folder sort mode
export function useUpdateFolderSort() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: updateFolderSort,
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

// Hook for creating a folder
export function useCreateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFolder,
    onSuccess: () => {
      // Invalidate user query to refresh folders list
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}
